import { isPlatformBrowser } from '@angular/common';
import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TimeUtils } from '@freelancer/time-utils';
import type { Subscription } from 'rxjs';
import { IconColor, IconSize } from '../icon';
import { FontColor, TextSize } from '../text';
import type { ToggleOption } from './toggle-buttons.types';
import { ToggleButtonsColor } from './toggle-buttons.types';

@Component({
  selector: 'fl-toggle-buttons',
  template: `
    <div
      class="Container"
      role="listbox"
      [attr.data-color]="color"
    >
      <div
        #toggleOptions
        *ngFor="let option of options; let index = index; let isLast = last"
        role="option"
        tabindex=""
        class="Option"
        [class.Selected]="control.value === option.option"
        [attr.aria-selected]="control.value === option.option"
        [attr.data-option-value]="option.option"
        [attr.data-color]="color"
        [attr.data-icons]="icons"
        [attr.data-full-width]="fullWidth"
        [flMarginRight]="isLast ? 'none' : 'xxxsmall'"
        (click)="changeValue(option)"
      >
        <ng-container *ngIf="icons; else default">
          <fl-icon
            [name]="option.value"
            [size]="iconSize"
            [color]="
              control.value === option.option
                ? color === ToggleButtonsColor.DARK
                  ? IconColor.LIGHT
                  : IconColor.FOREGROUND
                : color === ToggleButtonsColor.DARK
                  ? IconColor.LIGHT
                  : IconColor.DARK
            "
          ></fl-icon>
        </ng-container>

        <ng-template #default>
          <fl-text
            class="DarkText"
            [size]="textSize"
            [color]="
              control.value === option.option
                ? color === ToggleButtonsColor.DARK
                  ? FontColor.LIGHT
                  : FontColor.FOREGROUND
                : color === ToggleButtonsColor.DARK
                  ? FontColor.LIGHT
                  : FontColor.INHERIT
            "
          >
            {{ option.value }}
          </fl-text>
        </ng-template>
      </div>

      <div
        #buttonElement
        class="Button"
        [attr.data-color]="color"
        [ngStyle]="{
          'width.px': selectedOptionWidth,
          transform: buttonTransform,
        }"
      ></div>
    </div>
  `,
  styleUrls: ['./toggle-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleButtonsComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  FontColor = FontColor;
  IconColor = IconColor;
  TextSize = TextSize;
  ToggleButtonsColor = ToggleButtonsColor;

  @Input() color = ToggleButtonsColor.LIGHT;
  @Input() control: FormControl<string>;
  @Input() options: readonly ToggleOption[] = [];
  @Input() icons = false;
  @Input() iconSize = IconSize.MID;
  @Input() textSize = TextSize.XSMALL;

  @HostBinding('attr.data-full-width')
  @Input()
  fullWidth = false;

  private optionsSubscription?: Subscription;
  selectedOptionWidth = 0;
  buttonTransform = 'translateX(0)';

  @ViewChild('buttonElement') buttonElementRef!: ElementRef<HTMLDivElement>;
  @ViewChildren('toggleOptions') optionElements!: QueryList<
    ElementRef<HTMLDivElement>
  >;

  private resizeObserver?: ResizeObserver;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private time: TimeUtils,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('control' in changes) {
      this.optionsSubscription?.unsubscribe();
      this.optionsSubscription = this.control.valueChanges.subscribe(() =>
        this.updateButtonTransform(),
      );
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setupResizeObserver();
    }
    this.time.setTimeout(() => this.updateButtonTransform());
  }

  ngOnDestroy(): void {
    this.optionsSubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  private getSelectedElement(): ElementRef<HTMLDivElement> | undefined {
    return this.optionElements.find(
      element =>
        element.nativeElement.getAttribute('data-option-value') ===
        this.control.value,
    );
  }

  private setupResizeObserver(): void {
    const selectedElement = this.getSelectedElement();

    if (selectedElement?.nativeElement.clientWidth === 0) {
      // Element is not yet visible or has no width
      this.resizeObserver = new ResizeObserver(() => {
        // Upon resize, update the width and perform any necessary operations
        this.time.setTimeout(() => this.updateButtonTransform());
      });

      this.resizeObserver.observe(selectedElement?.nativeElement);
    }
  }

  updateButtonTransform(): void {
    const buttonElement = this.buttonElementRef.nativeElement;
    const selectedElement = this.getSelectedElement();

    if (selectedElement) {
      this.selectedOptionWidth = selectedElement.nativeElement.clientWidth;
      const transformOffset =
        selectedElement.nativeElement.offsetLeft - buttonElement.offsetLeft;
      this.buttonTransform = `translateX(${transformOffset}px)`;

      this.changeDetectorRef.markForCheck();
    }
  }

  changeValue(option: ToggleOption): void {
    this.control.setValue(option.option);
  }
}
